import * as Sentry from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { PurchaseStatus } from "@/app/payments/types";
import { Button } from "@/components/Button";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { PersistentDrawer } from "@/components/Drawer";
import { BookingError } from "@/components/Errors/BookingError";
import { Image } from "@/components/Image/Image";
import { OTPPopup } from "@/components/OTPPopup/OTPPopup";
import { PaymentChooser } from "@/components/PaymentChooser/PaymentChooser";
import {
  PaymentDetailsMonthlyPaymentRow,
  PaymentDetailsTotalPriceRow,
} from "@/components/PaymentDetails";
import { PriceChangedModal } from "@/components/PriceChangedModal/PriceChangedModal";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { SearchDataMissingError } from "@/errors/SearchDataMissingError";
import { HotelBookingSuccessSkeleton } from "@/features/hotels/components/hotel/HotelBookingSuccessSkeleton";
import { HotelOfferError } from "@/features/hotels/components/hotel/HotelOfferError";
import { PaymentSummaryCard } from "@/features/hotels/components/hotel/PaymentSummaryCard";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelTravellersSelectionContext } from "@/features/hotels/contexts/HotelTravellersSelectionContext";
import { useHotelBooking } from "@/features/hotels/hooks/useHotelBooking";
import { useHotelRoomAvailabilityMutation } from "@/features/hotels/hooks/useHotelRoomAvailability";
import { useHotelSearch } from "@/features/hotels/hooks/useHotelSearch";
import {
  assignTravellersToRooms,
  TravellerAssignmentError,
} from "@/features/hotels/utils/assignTravellersToRooms";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { useCreatePendingInstallment } from "@/hooks/useCreatePendingInstallment";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import {
  BnplTrackingEvents,
  HotelTrackingEvents,
  rudderAnalytics,
} from "@/lib/rudderstack";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/payment",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const intl = useIntl();

  const { searchResultKey, searchResultToken } = useHotelSearch();
  const {
    data: booking,
    availabilityToken,
    promoCode,
  } = useHotelBookingContext();
  const { selectedTravellers, leadTravellerId } =
    useHotelTravellersSelectionContext();
  const { data: hotel } = useHotelDataContext();
  const { updatePurchaseState, purchaseState } = usePurchaseStateContext();
  const { searchData } = useHotelSearchDataContext();

  if (
    !searchData ||
    !availabilityToken ||
    !booking ||
    !hotel ||
    !searchResultKey ||
    !searchResultToken
  ) {
    throw new SearchDataMissingError("Hotel search data is missing");
  }

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const {
    isEligible,
    paymentType,
    sessionId,
    refreshSession,
    selectedInstallments,
  } = usePaymentContext();

  const travelDate = new Date(searchData.dates.checkIn);
  const checkOutDate = new Date(searchData.dates.checkOut);

  const {
    data: promoCodeData,
    validationStatus: promoCodeValidationStatus,
    revalidate,
  } = usePromoCodeValidation({
    service: "hotels",
    validationRequestData: {
      promoCode: promoCode || "",
      availabilityToken: availabilityToken || "",
    },
  });

  const paymentMethod = paymentType === "bnpl" ? "bnpl" : "qiAccount";

  const { qiPayment, checkoutPayment, bnplPayment } = useHotelBooking({
    method: paymentMethod,
    initialAvailabilityToken: availabilityToken,
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        if (paymentType === "bnpl") {
          // to update eligibility info
          refreshSession();
        }
        navigate({
          to: "/hotels/$id/confirmation",
          search: { orderId },
        }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  const {
    data: availability,
    isError: isAvailabilityError,
    error: availabilityError,
    isPending: isAvailabilityRefetching,
    mutate: refetchAvailability,
  } = useHotelRoomAvailabilityMutation({
    onSuccess: (data) => {
      if (data && booking) {
        const latestTotalPrice = data.totalPrice;
        if (latestTotalPrice !== calculateBookingPrice(booking).value) {
          revalidate();
          setShowConfirmation(true);
        } else {
          handlePurchase(availabilityToken);
        }
      }
    },
  });

  const { mutateAsync: createPendingInstallment } =
    useCreatePendingInstallment();

  const promoCodeToApply = promoCodeData?.result
    ? promoCode || undefined
    : undefined;

  const hotelTotalPrice = calculateBookingPrice(booking);
  const basePrice = hotelTotalPrice.value;
  const totalPrice = promoCodeData?.paymentAmount || basePrice;

  const createBookingPayload = (availabilityToken: string) => {
    if (!leadTravellerId) return null;

    const rooms = searchData.rooms;

    try {
      return {
        promoCode: promoCodeToApply,
        availabilityToken: availabilityToken,
        searchResultKey,
        searchResultToken,
        hotelId: hotel.id,
        offerId: booking.offerId,
        checkInDate: travelDate,
        rooms: assignTravellersToRooms(
          booking.rooms.map((bookingRoom, index) => ({
            token: bookingRoom.roomToken,
            adults: rooms[index]?.adults ?? 0,
            children: rooms[index]?.children ?? 0,
          })),
          selectedTravellers,
          leadTravellerId,
          travelDate,
        ),
      };
    } catch (error) {
      if (error instanceof TravellerAssignmentError) {
        return null;
      } else {
        throw error;
      }
    }
  };

  const handlePurchase = async (availabilityToken?: string | null) => {
    if (purchaseState === "completed" || !availabilityToken) {
      return;
    }

    setIsContinuing(true);

    try {
      if (paymentMethod === "bnpl") {
        const { data: eligibilityData } = await refreshSession();
        if (eligibilityData?.isEligible) {
          await createPendingInstallment({
            sessionId: eligibilityData.sessionId,
            amount: basePrice,
            countOfMonth: selectedInstallments,
          });

          setShowOTP(true);
        }

        return;
      }

      const payload = createBookingPayload(availabilityToken);
      if (!payload) return;

      rudderAnalytics.track(
        HotelTrackingEvents.USER_PROCEED_TO_PAYMENT_SCREEN,
        {
          amount: hotelTotalPrice.value,
          payment_method: paymentMethod,
          currency: hotelTotalPrice.currency,
        },
      );

      if (paymentMethod === "qiAccount") {
        await qiPayment.mutateAsync(payload);
      } else {
        await checkoutPayment.submit(payload);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsContinuing(false);
    }
  };

  if (isAvailabilityError) {
    return <HotelOfferError error={availabilityError} />;
  }

  if (qiPayment.isError) {
    return <BookingError error={qiPayment.error} type="hotels" />;
  }

  if (
    qiPayment.isPending ||
    checkoutPayment.isPending ||
    bnplPayment.isPending
  ) {
    return (
      <div className="px-main">
        <HotelBookingSuccessSkeleton />
      </div>
    );
  }

  return (
    <div className="px-main">
      <div className="mb-4 text-xl font-medium">
        {intl.formatMessage({
          defaultMessage: "Payment method",
          description: "Page heading for the hotel payment page",
        })}
      </div>

      <div className="flex w-full flex-col gap-2.5">
        <div className="section-card flex flex-col gap-4 pb-2">
          <div className="flex justify-between gap-4">
            <div>
              <div className="mb-1 text-base font-medium text-text-primary">
                {hotel.name}
              </div>

              <div className="text-sm text-text-tertiary">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      "{checkIn, date, ::EEEMMMd} - {checkOut, date, ::EEEMMMd}",
                    description:
                      "Check-in to check-out date range on the hotel payment page (e.g. 'Tue, Mar 31 - Wed, Apr 1')",
                  },
                  {
                    checkIn: travelDate,
                    checkOut: checkOutDate,
                  },
                )}
              </div>
            </div>

            <Image
              className="size-17.5 shrink-0 rounded-lg object-cover"
              src={hotel.mainImageUrl || "/imagePlaceholder.svg"}
              alt={hotel.name}
            />
          </div>
        </div>

        <PaymentChooser
          price={totalPrice}
          onChange={(paymentType) => {
            const eventName =
              paymentType === "bnpl"
                ? BnplTrackingEvents.USER_SELECTED_BNPL_PAYMENT
                : BnplTrackingEvents.USER_SELECTED_STANDARD_PAYMENT;

            rudderAnalytics.track(eventName, {
              is_bnpl_eligible: isEligible,
            });
          }}
        />

        <PaymentSummaryCard
          basePrice={basePrice}
          promoCodeDiscount={promoCodeData?.discountAmount}
          summary={
            paymentType === "bnpl" ? (
              <PaymentDetailsMonthlyPaymentRow
                totalPrice={totalPrice}
                selectedInstallments={selectedInstallments}
              />
            ) : (
              <PaymentDetailsTotalPriceRow totalPrice={totalPrice} />
            )
          }
        />
      </div>

      {checkoutPayment.isLoadingCheckoutForm ? (
        <CircularProgress size={20} />
      ) : null}
      <div id="flow-container" className="section-card p-0 not-empty:mt-2" />

      <PriceChangedModal
        open={showConfirmation}
        price={formatCurrency(availability?.data.data.totalPrice)}
        onCancel={() => {
          setShowConfirmation(false);
          navigate({ to: "/hotels/search" });
        }}
        onConfirm={() => {
          handlePurchase(availability?.data.data.availabilityToken);
        }}
      />
      <OTPPopup
        isOpen={showOTP}
        onClose={() => {
          setShowOTP(false);
        }}
        onSubmit={(otp) => {
          const payload = createBookingPayload(availabilityToken);

          if (!sessionId || !payload) {
            return;
          }

          bnplPayment.mutate({
            ...payload,
            sessionId,
            otp,
          });
        }}
        isSubmitting={bnplPayment.isPending}
        error={
          bnplPayment.isError
            ? intl.formatMessage({
                defaultMessage: "OTP verification failed",
                description: "Error message shown when OTP verification fails",
              })
            : undefined
        }
      />

      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            refetchAvailability({
              hotelId: `${hotel?.id}`,
              searchResultKey,
              searchResultToken,
              packageToken: booking.packageToken,
              offerId: booking.offerId,
              roomTokens: booking.rooms.map((room) => room.roomToken),
            });
          }}
          disabled={promoCodeValidationStatus?.type === "loading"}
          loading={isAvailabilityRefetching || isContinuing}
        >
          {intl.formatMessage({
            defaultMessage: "Book and Pay",
            description:
              "Label for the button that confirms the hotel booking and proceeds to payment",
          })}
        </Button>
      </PersistentDrawer>
    </div>
  );
}
